// ========================
// World Map UI - POE-style Act/Zone Panel
// ========================

import { Renderer } from '@client/core/Renderer';
import { InputManager } from '@client/input/InputManager';
import { ZONES, ZoneType } from '@shared/world/Zone';
import { getWaypointManager, WAYPOINT_ZONES } from '@shared/world/WaypointManager';
import { t } from '@locales/index';

// ---- Data ----

interface ActInfo {
  id: number;
  zones: ZoneType[];
}

const ACTS: ActInfo[] = [
  { id: 1, zones: ['otag_koyu', 'karanlik_orman', 'ruh_batakligi', 'erlik_tapinagi'] },
  { id: 2, zones: ['ergenekon_girisi', 'firtina_ovasi', 'ates_magarasi', 'yersub_tahti'] },
  { id: 3, zones: ['karanlik_tunel', 'olum_nehri', 'kemik_ormani', 'lanetli_saray'] },
  { id: 4, zones: ['erlik_kapisi', 'cehennem_bahcesi', 'taht_odasi'] },
];

type BiomeKey = 'forest' | 'swamp' | 'temple' | 'mountain' | 'fire' | 'underground' | 'hell' | 'village' | 'steppe';

const BIOME_COLORS: Record<BiomeKey, string> = {
  village: '#a89060',
  forest: '#2d8a27',
  swamp: '#3a7a5a',
  temple: '#8a4aaa',
  mountain: '#7a8a9a',
  steppe: '#8a9a4a',
  fire: '#cc5522',
  underground: '#7a4aaa',
  hell: '#cc3333',
};

function getZoneBiome(id: ZoneType): BiomeKey {
  switch (id) {
    case 'otag_koyu': return 'village';
    case 'karanlik_orman': return 'forest';
    case 'ruh_batakligi': return 'swamp';
    case 'erlik_tapinagi': return 'temple';
    case 'ergenekon_girisi': return 'mountain';
    case 'firtina_ovasi': return 'steppe';
    case 'ates_magarasi': return 'fire';
    case 'yersub_tahti': return 'underground';
    case 'karanlik_tunel': return 'underground';
    case 'olum_nehri': return 'swamp';
    case 'kemik_ormani': return 'forest';
    case 'lanetli_saray': return 'underground';
    case 'erlik_kapisi': return 'hell';
    case 'cehennem_bahcesi': return 'hell';
    case 'taht_odasi': return 'hell';
    default: return 'village';
  }
}

// ---- Hit rects ----

interface Rect { x: number; y: number; w: number; h: number }

function hitTest(mx: number, my: number, r: Rect): boolean {
  return mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h;
}

// ---- Component ----

export class WorldMapUI {
  private isOpen = false;
  private currentZoneId: ZoneType = 'otag_koyu';
  private selectedAct = 0; // index into ACTS
  private hoveredZone: ZoneType | null = null;
  private animTime = 0;

  // Teleport callback
  public onTeleport: ((zoneId: ZoneType) => void) | null = null;

  // Hit areas (recalculated each frame)
  private actTabRects: Rect[] = [];
  private zoneRowRects: { zoneId: ZoneType; rect: Rect; btnRect: Rect }[] = [];
  private hoveredActTab = -1;
  private hoveredBtn: ZoneType | null = null;
  private closeRect: Rect = { x: 0, y: 0, w: 0, h: 0 };
  private hoveredClose = false;

  // Layout
  private panelW = 520;
  private panelH = 480;
  private panelX = 0;
  private panelY = 0;

  private readonly TITLE_H = 48;
  private readonly TAB_H = 40;
  private readonly ZONE_ROW_H = 62;
  private readonly FOOTER_H = 36;
  private readonly PAD = 16;

  constructor() {}

  // ---- Public API (unchanged signatures) ----

  open(currentRegion: string, _playerLevel: number): void {
    this.isOpen = true;
    this.currentZoneId = currentRegion as ZoneType;
    // Auto-select act of current zone
    for (let i = 0; i < ACTS.length; i++) {
      if (ACTS[i].zones.includes(this.currentZoneId)) {
        this.selectedAct = i;
        break;
      }
    }
  }

  close(): void {
    this.isOpen = false;
  }

  toggle(currentRegion: string, playerLevel: number): void {
    if (this.isOpen) this.close();
    else this.open(currentRegion, playerLevel);
  }

  isVisible(): boolean { return this.isOpen; }

  getCurrentRegion(): string { return this.currentZoneId; }

  setCurrentRegion(regionId: string): void { this.currentZoneId = regionId as ZoneType; }

  // ---- Update ----

  update(deltaTime: number, input: InputManager): void {
    if (!this.isOpen) return;
    this.animTime = (this.animTime + deltaTime) % 6283;

    const m = input.getMousePosition();

    // Hover: close button
    this.hoveredClose = hitTest(m.x, m.y, this.closeRect);

    // Hover: act tabs
    this.hoveredActTab = -1;
    for (let i = 0; i < this.actTabRects.length; i++) {
      if (hitTest(m.x, m.y, this.actTabRects[i])) this.hoveredActTab = i;
    }

    // Hover: zone rows & travel buttons
    this.hoveredZone = null;
    this.hoveredBtn = null;
    for (const zr of this.zoneRowRects) {
      if (hitTest(m.x, m.y, zr.rect)) this.hoveredZone = zr.zoneId;
      if (hitTest(m.x, m.y, zr.btnRect)) this.hoveredBtn = zr.zoneId;
    }

    // Click
    if (input.wasMouseClicked()) {
      if (this.hoveredClose) { this.close(); return; }
      if (this.hoveredActTab >= 0) { this.selectedAct = this.hoveredActTab; return; }
      if (this.hoveredBtn && this.hoveredBtn !== this.currentZoneId) {
        if (this.onTeleport) { this.onTeleport(this.hoveredBtn); this.close(); }
        return;
      }
    }

    if (input.isKeyPressed('Escape')) this.close();
  }

  // ---- Render ----

  render(renderer: Renderer): void {
    if (!this.isOpen) return;
    const ctx = renderer.context;
    const W = renderer.width;
    const H = renderer.height;

    // Responsive sizing
    this.panelW = Math.min(540, W - 60);
    this.panelH = Math.min(500, H - 60);
    this.panelX = (W - this.panelW) / 2;
    this.panelY = (H - this.panelH) / 2;

    // Dim
    ctx.fillStyle = 'rgba(0,0,0,0.8)';
    ctx.fillRect(0, 0, W, H);

    // Shadow
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 8;

    // Panel BG
    ctx.fillStyle = '#12100e';
    ctx.beginPath();
    ctx.roundRect(this.panelX, this.panelY, this.panelW, this.panelH, 10);
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Panel border
    ctx.strokeStyle = '#3a3028';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(this.panelX, this.panelY, this.panelW, this.panelH, 10);
    ctx.stroke();

    // Gold accent border (inner)
    ctx.strokeStyle = '#c4a030';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(this.panelX + 3, this.panelY + 3, this.panelW - 6, this.panelH - 6, 8);
    ctx.stroke();

    this.drawTitle(ctx);
    this.drawActTabs(ctx);
    this.drawZoneList(ctx);
    this.drawFooter(ctx);
  }

  // ---- Title Bar ----

  private drawTitle(ctx: CanvasRenderingContext2D): void {
    const x = this.panelX;
    const y = this.panelY;
    const w = this.panelW;

    // Title bg
    ctx.fillStyle = '#1a1610';
    ctx.beginPath();
    ctx.roundRect(x + 4, y + 4, w - 8, this.TITLE_H, [7, 7, 0, 0]);
    ctx.fill();

    // Title text
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('worldMap.title'), x + w / 2, y + this.TITLE_H / 2 + 2);

    // Close button
    const bs = 28;
    const bx = x + w - bs - 12;
    const by = y + (this.TITLE_H - bs) / 2 + 2;
    this.closeRect = { x: bx, y: by, w: bs, h: bs };

    ctx.fillStyle = this.hoveredClose ? '#3a2a1a' : '#221c14';
    ctx.beginPath();
    ctx.roundRect(bx, by, bs, bs, 5);
    ctx.fill();

    ctx.strokeStyle = this.hoveredClose ? '#d4af37' : '#6a5a40';
    ctx.lineWidth = 2;
    const cx = bx + bs / 2;
    const cy = by + bs / 2;
    const s = 6;
    ctx.beginPath();
    ctx.moveTo(cx - s, cy - s); ctx.lineTo(cx + s, cy + s);
    ctx.moveTo(cx + s, cy - s); ctx.lineTo(cx - s, cy + s);
    ctx.stroke();
  }

  // ---- Act Tabs ----

  private drawActTabs(ctx: CanvasRenderingContext2D): void {
    const tabY = this.panelY + this.TITLE_H;
    const tabW = (this.panelW - 8) / ACTS.length;
    this.actTabRects = [];

    // Tab bar bg
    ctx.fillStyle = '#0e0c0a';
    ctx.fillRect(this.panelX + 4, tabY, this.panelW - 8, this.TAB_H);

    for (let i = 0; i < ACTS.length; i++) {
      const tx = this.panelX + 4 + i * tabW;
      const isSel = i === this.selectedAct;
      const isHov = i === this.hoveredActTab;

      this.actTabRects.push({ x: tx, y: tabY, w: tabW, h: this.TAB_H });

      // Tab bg
      if (isSel) {
        ctx.fillStyle = '#221e18';
      } else if (isHov) {
        ctx.fillStyle = '#1a1610';
      } else {
        ctx.fillStyle = 'transparent';
      }
      if (isSel || isHov) {
        ctx.fillRect(tx, tabY, tabW, this.TAB_H);
      }

      // Active indicator (bottom gold bar)
      if (isSel) {
        ctx.fillStyle = '#d4af37';
        ctx.fillRect(tx + 8, tabY + this.TAB_H - 3, tabW - 16, 3);
      }

      // Tab text
      ctx.fillStyle = isSel ? '#d4af37' : (isHov ? '#c0a050' : '#706050');
      ctx.font = `bold 14px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        t('waypoint.actLabel', { num: ACTS[i].id }),
        tx + tabW / 2,
        tabY + this.TAB_H / 2
      );

      // Separator
      if (i < ACTS.length - 1) {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tx + tabW, tabY + 8);
        ctx.lineTo(tx + tabW, tabY + this.TAB_H - 8);
        ctx.stroke();
      }
    }

    // Bottom line
    ctx.strokeStyle = '#3a3028';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.panelX + 4, tabY + this.TAB_H);
    ctx.lineTo(this.panelX + this.panelW - 4, tabY + this.TAB_H);
    ctx.stroke();
  }

  // ---- Zone List ----

  private drawZoneList(ctx: CanvasRenderingContext2D): void {
    const act = ACTS[this.selectedAct];
    if (!act) return;

    const listX = this.panelX + this.PAD;
    const listY = this.panelY + this.TITLE_H + this.TAB_H + 8;
    const listW = this.panelW - this.PAD * 2;
    const listH = this.panelH - this.TITLE_H - this.TAB_H - this.FOOTER_H - 16;

    // Clip
    ctx.save();
    ctx.beginPath();
    ctx.rect(listX - 2, listY - 2, listW + 4, listH + 4);
    ctx.clip();

    this.zoneRowRects = [];

    for (let i = 0; i < act.zones.length; i++) {
      const zoneId = act.zones[i];
      const zone = ZONES[zoneId];
      const ry = listY + i * this.ZONE_ROW_H;

      if (ry + this.ZONE_ROW_H < listY || ry > listY + listH) continue;

      const rowRect: Rect = { x: listX, y: ry, w: listW, h: this.ZONE_ROW_H - 4 };
      const isCurrent = zoneId === this.currentZoneId;
      const isHovered = zoneId === this.hoveredZone;
      const isBoss = !!zone.bossType;
      const biomeColor = BIOME_COLORS[getZoneBiome(zoneId)];
      const wpInfo = WAYPOINT_ZONES[zoneId];
      const hasWP = wpInfo?.hasWaypoint ?? false;
      const wpActive = hasWP && getWaypointManager().isWaypointActivated(zoneId);

      // Row background
      if (isCurrent) {
        ctx.fillStyle = '#1a2a18';
      } else if (isHovered) {
        ctx.fillStyle = '#1e1a14';
      } else {
        ctx.fillStyle = (i % 2 === 0) ? '#151310' : '#131110';
      }
      ctx.beginPath();
      ctx.roundRect(rowRect.x, rowRect.y, rowRect.w, rowRect.h, 6);
      ctx.fill();

      // Row border
      if (isCurrent) {
        ctx.strokeStyle = '#3a8a3a';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(rowRect.x, rowRect.y, rowRect.w, rowRect.h, 6);
        ctx.stroke();
      } else if (isHovered) {
        ctx.strokeStyle = '#3a3228';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(rowRect.x, rowRect.y, rowRect.w, rowRect.h, 6);
        ctx.stroke();
      }

      // Biome color strip (left)
      ctx.fillStyle = biomeColor;
      ctx.beginPath();
      ctx.roundRect(rowRect.x, rowRect.y, 5, rowRect.h, [6, 0, 0, 6]);
      ctx.fill();

      // Waypoint indicator
      const iconX = rowRect.x + 22;
      const iconY = rowRect.y + rowRect.h / 2;
      if (hasWP) {
        if (wpActive) {
          ctx.fillStyle = '#44aaff';
          ctx.beginPath();
          ctx.arc(iconX, iconY, 6, 0, Math.PI * 2);
          ctx.fill();
          // Inner diamond
          ctx.fillStyle = '#aaddff';
          ctx.beginPath();
          ctx.moveTo(iconX, iconY - 3);
          ctx.lineTo(iconX + 2, iconY);
          ctx.lineTo(iconX, iconY + 3);
          ctx.lineTo(iconX - 2, iconY);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.strokeStyle = '#444';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(iconX, iconY, 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = '#555';
          ctx.font = '8px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('?', iconX, iconY + 1);
        }
      } else {
        ctx.strokeStyle = '#2a2520';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(iconX - 4, iconY);
        ctx.lineTo(iconX + 4, iconY);
        ctx.stroke();
      }

      // Zone name
      const textX = rowRect.x + 40;
      ctx.fillStyle = isCurrent ? '#5aee5a' : (isHovered ? '#fff' : '#c0b8a0');
      ctx.font = `${isCurrent ? 'bold ' : ''}14px sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const zoneName = t(`zones.${zoneId}`) || zone.name;
      ctx.fillText(zoneName, textX, rowRect.y + 20);

      // Level range + boss tag
      ctx.fillStyle = isCurrent ? '#6a9a6a' : '#706858';
      ctx.font = '11px sans-serif';
      let subText = t('waypoint.levelRange', { min: zone.levelRange.min, max: zone.levelRange.max });
      if (isBoss) subText += '  \u2620 ' + t('worldMap.bossZone');
      ctx.fillText(subText, textX, rowRect.y + 40);

      // Right side: current badge OR travel button
      const rightX = rowRect.x + rowRect.w;

      if (isCurrent) {
        // "You are here" badge
        ctx.fillStyle = '#1a3a1a';
        const badgeW = 90;
        const badgeH = 24;
        const badgeX = rightX - badgeW - 10;
        const badgeY = rowRect.y + (rowRect.h - badgeH) / 2;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 4);
        ctx.fill();
        ctx.strokeStyle = '#3a8a3a';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 4);
        ctx.stroke();
        ctx.fillStyle = '#5aee5a';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('waypoint.currentZone'), badgeX + badgeW / 2, badgeY + badgeH / 2);

        // No btn for current
        this.zoneRowRects.push({ zoneId, rect: rowRect, btnRect: { x: 0, y: 0, w: 0, h: 0 } });
      } else {
        // Travel button
        const btnW = 90;
        const btnH = 28;
        const btnX = rightX - btnW - 10;
        const btnY = rowRect.y + (rowRect.h - btnH) / 2;
        const btnRect: Rect = { x: btnX, y: btnY, w: btnW, h: btnH };
        const btnHov = this.hoveredBtn === zoneId;

        ctx.fillStyle = btnHov ? '#2a6a2a' : '#1a3a1a';
        ctx.beginPath();
        ctx.roundRect(btnX, btnY, btnW, btnH, 5);
        ctx.fill();

        ctx.strokeStyle = btnHov ? '#44cc44' : '#2a6a2a';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(btnX, btnY, btnW, btnH, 5);
        ctx.stroke();

        ctx.fillStyle = btnHov ? '#fff' : '#88ee88';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('worldMap.travelTo'), btnX + btnW / 2, btnY + btnH / 2);

        this.zoneRowRects.push({ zoneId, rect: rowRect, btnRect });
      }
    }

    ctx.restore();
  }

  // ---- Footer ----

  private drawFooter(ctx: CanvasRenderingContext2D): void {
    const fy = this.panelY + this.panelH - this.FOOTER_H;

    // Separator
    ctx.strokeStyle = '#2a2520';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.panelX + this.PAD, fy);
    ctx.lineTo(this.panelX + this.panelW - this.PAD, fy);
    ctx.stroke();

    ctx.fillStyle = '#605848';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('worldMap.instructions'), this.panelX + this.panelW / 2, fy + this.FOOTER_H / 2);
  }
}
